import { Suspense, useMemo, useState } from "react";
import { AlertCircle, ShieldCheck, TrendingDown, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router";
import { useGetIdentity } from "ra-core";

import { CabecalhoLista, CabecalhoPagina, CardBento, Rotulo, Sub, ValorGrande, VerMais } from "../bento/ui";
import { formatCurrency } from "../dashboard/blocos/tipos";
import { CurvaMes, CurvaMeses } from "../dashboard/charts";
import { janelaDoPeriodo, serieAcumulada, somaDoPeriodo, type Periodo } from "../dashboard/bento/periodo";
import { SeletorPeriodo } from "../dashboard/bento/SeletorPeriodo";
import { BotaoOlhinho, ValoresOcultosProvider } from "../valores-ocultos/ValoresOcultos";
import { useMascara } from "../valores-ocultos/contexto";
import { BadgeSeguradora, Card, CardContent, PageSpinner, formatarDocumento } from "./comuns";
import { FiltroSeguradora, useSeguradoraFiltro } from "./FiltroSeguradora";
import { chaveDoMes, resumirSeguros, type ApolicePainel, type ComissaoPainel, type PropostaPainel, type ResumoSeguros } from "./painelSeguros";
import { rotuloSeguradora } from "./seguradoras";
import { useTabelaSeguros } from "./useTabelaSeguros";

/**
 * Dashboard de Seguros (03/11/2026) — o do Consórcio com a régua de seguros:
 * o gráfico grande é a produção (prêmio novo) com Mês | Semestre | Ano, ao
 * lado a faixa de quatro indicadores (carteira, inadimplência, propostas e
 * comissão), embaixo a produção por corretor e a cobrança crítica.
 *
 * Quem vê: o super admin (a rede inteira) e quem tem corretora vinculada (a
 * carteira dela, pela RLS da 206). As contas estão em painelSeguros.ts.
 */

/** A faixa dos indicadores do Dashboard do Consórcio (HomeDashboard). */
const FAIXA_INDICADORES =
  "grid grid-cols-1 gap-px overflow-hidden bg-border cartao border-t-ouro " +
  "[&>div]:!rounded-none [&>div]:!border-0 [&>div]:!shadow-none";

const APOLICES = { uma: "apólice", varias: "apólices" };
const TOP_CORRETORES = 8;

const pct = (v: number) => `${v.toFixed(1).replace(".", ",")}%`;

export const PainelSeguros = () => (
  <ValoresOcultosProvider>
    <Conteudo />
  </ValoresOcultosProvider>
);

const Conteudo = () => {
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const { seguradora, definir } = useSeguradoraFiltro();
  const hoje = new Date();
  const mesAtual = chaveDoMes(hoje);
  const mesAnterior = chaveDoMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));

  const apolices = useTabelaSeguros<ApolicePainel>("seguros_apolices_v", {
    select:
      "seguradora, numero, status, produto_nome, premio_mensal, inicio_vigencia, dias_atraso, faturas_em_atraso, segurado_nome, segurado_cpf_digits, corretor_documento_digits, corretor_nome",
    seguradora,
  });
  const propostas = useTabelaSeguros<PropostaPainel>("seguros_propostas_v", {
    select: "seguradora, numero, status, premio_mensal",
    seguradora,
  });
  // Só as duas competências que a tela mostra: o histórico inteiro de
  // comissões passa de dezenas de milhares de linhas.
  const comissoesMes = useTabelaSeguros<ComissaoPainel>("seguros_comissoes_v", {
    select: "competencia, valor_comissao, tipo_lancamento",
    igual: { competencia: mesAtual },
    seguradora,
  });
  const comissoesAnterior = useTabelaSeguros<ComissaoPainel>("seguros_comissoes_v", {
    select: "competencia, valor_comissao, tipo_lancamento",
    igual: { competencia: mesAnterior },
    seguradora,
  });

  const resumo = useMemo(
    () => resumirSeguros(apolices.linhas, propostas.linhas, [...comissoesMes.linhas, ...comissoesAnterior.linhas]),
    [apolices.linhas, propostas.linhas, comissoesMes.linhas, comissoesAnterior.linhas],
  );

  const carregando = apolices.carregando || propostas.carregando || comissoesMes.carregando || comissoesAnterior.carregando;
  const mesExtenso = hoje.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const cabecalho = (
    <CabecalhoPagina
      titulo="Dashboard"
      aoLadoDoTitulo={<BotaoOlhinho />}
      descricao={`Seguros · ${identity?.is_super_admin ? "toda a rede" : "carteira da corretora"} · ${mesExtenso}`}
      acoes={<FiltroSeguradora valor={seguradora} aoMudar={definir} />}
    />
  );

  if (carregando) return <div className="space-y-4">{cabecalho}<PageSpinner /></div>;

  if (apolices.linhas.length === 0) {
    return (
      <div className="space-y-4">
        {cabecalho}
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <ShieldCheck className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">Nenhuma apólice na carteira.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {cabecalho}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <CardProducao resumo={resumo} navigate={navigate} />
        <div className={`${FAIXA_INDICADORES} md:col-span-2 xl:row-span-2 sm:grid-cols-2`}>
          <CardCarteira resumo={resumo} navigate={navigate} />
          <CardRisco resumo={resumo} navigate={navigate} />
          <CardPropostas resumo={resumo} navigate={navigate} />
          <CardComissao resumo={resumo} navigate={navigate} />
        </div>
        <CardCorretores resumo={resumo} />
        <CardCobrancaCritica resumo={resumo} todas={seguradora === "todas"} navigate={navigate} />
      </div>
    </div>
  );
};

type PropsCard = { resumo: ResumoSeguros; navigate: (to: string) => void };

const CardProducao = ({ resumo, navigate }: PropsCard) => {
  const m = useMascara();
  const [periodo, setPeriodo] = useState<Periodo>("mes");
  const janela = useMemo(() => janelaDoPeriodo(periodo), [periodo]);
  const total = useMemo(() => somaDoPeriodo(resumo.producaoPorMes, janela), [resumo.producaoPorMes, janela]);
  const curva = useMemo(() => serieAcumulada(resumo.producaoPorMes, janela), [resumo.producaoPorMes, janela]);
  const ano = useMemo(() => somaDoPeriodo(resumo.producaoPorMes, janelaDoPeriodo("ano")), [resumo.producaoPorMes]);

  const anterior = resumo.mesAnteriorMesmoPeriodo;
  const varPct = periodo === "mes" && anterior > 0 ? ((total.valor - anterior) * 100) / anterior : null;
  const nomeMes = (d: Date) => d.toLocaleDateString("pt-BR", { month: "long" });
  const hoje = new Date();
  const titulo =
    periodo === "mes"
      ? `Produção em ${nomeMes(hoje)}`
      : periodo === "semestre"
        ? `Produção no ${janela.semestre}º semestre`
        : `Produção em ${janela.ano}`;

  return (
    <CardBento col={2} className="flex flex-col !px-5 !py-4 xl:row-span-2">
      <div className="flex flex-wrap-reverse items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[200px]">
          <Rotulo>{titulo}</Rotulo>
          <button
            type="button"
            onClick={() => navigate("/seguros/apolices")}
            className="mt-1.5 cursor-pointer text-[26px] font-bold leading-none tracking-[-0.035em] tabular-nums hover:text-primary sm:text-[34px]"
          >
            {m(formatCurrency(total.valor, true))}
            <span className="text-base font-normal text-muted-foreground">/mês</span>
          </button>
          <p className="mt-2 flex flex-wrap items-center gap-[7px] text-[12.5px] text-[#6b7480] dark:text-muted-foreground">
            {varPct != null && (
              <>
                <span
                  className={`inline-flex items-center gap-[3px] rounded-full px-2 py-0.5 font-semibold ${
                    varPct >= 0 ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fef3c7] text-[#b45309]"
                  }`}
                >
                  {varPct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {m(pct(Math.abs(varPct)))}
                </span>
                <span>vs. {nomeMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1))} até o dia {hoje.getDate()}</span>
                <span>·</span>
              </>
            )}
            <span>
              {m(total.count)} {total.count === 1 ? "apólice nova" : "apólices novas"}
            </span>
            <span>·</span>
            <span>{m(formatCurrency(total.valor * 12, true))} anualizado</span>
          </p>
          {periodo !== "ano" && (
            <Sub className="!mt-1.5">
              {janela.ano}:{" "}
              <span className="font-semibold text-foreground">{m(formatCurrency(ano.valor, true))}/mês</span> ·{" "}
              {m(ano.count)} {ano.count === 1 ? "apólice" : "apólices"}
            </Sub>
          )}
        </div>
        <SeletorPeriodo valor={periodo} onChange={setPeriodo} />
      </div>
      <div className="mt-2 min-h-[170px] flex-1">
        <Suspense fallback={<div style={{ height: 170 }} />}>
          {periodo === "mes" ? (
            <CurvaMes dados={resumo.producaoDiaria} />
          ) : (
            <CurvaMeses dados={curva} unidade={APOLICES} />
          )}
        </Suspense>
      </div>
    </CardBento>
  );
};

const CardCarteira = ({ resumo, navigate }: PropsCard) => {
  const m = useMascara();
  const total = resumo.porSeguradora.reduce((s, x) => s + x.premio, 0) || 1;
  return (
    <CardBento>
      <button type="button" onClick={() => navigate("/seguros/apolices")} className="block w-full cursor-pointer text-left">
        <Rotulo>Carteira vigente</Rotulo>
        <ValorGrande unidade="/mês">{m(formatCurrency(resumo.carteiraMensal, true))}</ValorGrande>
        <Sub>
          {m(resumo.vigentes)} {resumo.vigentes === 1 ? "apólice vigente" : "apólices vigentes"} ·{" "}
          {m(formatCurrency(resumo.carteiraMensal * 12, true))}/ano
        </Sub>
        {resumo.porSeguradora.length > 1 && (
          <Sub className="!mt-1">
            {resumo.porSeguradora
              .map((s) => `${rotuloSeguradora(s.seguradora)} ${m(pct((s.premio * 100) / total))}`)
              .join(" · ")}
          </Sub>
        )}
      </button>
    </CardBento>
  );
};

const CardRisco = ({ resumo, navigate }: PropsCard) => {
  const m = useMascara();
  const fatiaPct = resumo.carteiraMensal > 0 ? (resumo.premioEmRisco * 100) / resumo.carteiraMensal : 0;
  // O anel do card de Inadimplência do Consórcio: r=17, circunferência ≈ 106,8.
  const fatia = Math.min(106.8, (fatiaPct / 100) * 106.8);
  return (
    <CardBento>
      <button type="button" onClick={() => navigate("/seguros/cobranca")} className="block w-full cursor-pointer text-left">
        <Rotulo>Inadimplência</Rotulo>
        <div className="mt-2.5 flex items-center gap-3.5">
          <svg viewBox="0 0 42 42" className="h-[46px] w-[46px] shrink-0">
            <circle cx="21" cy="21" r="17" fill="none" stroke="var(--primary)" strokeOpacity="0.12" strokeWidth="6" />
            {fatia > 0 && (
              <circle
                cx="21"
                cy="21"
                r="17"
                fill="none"
                stroke="#b45309"
                strokeWidth="6"
                strokeDasharray={`${fatia} 106.8`}
                strokeLinecap="round"
                transform="rotate(-90 21 21)"
              />
            )}
          </svg>
          <div>
            <p className="text-[22px] font-bold leading-none tracking-[-0.03em] tabular-nums">{m(pct(fatiaPct))}</p>
            <Sub className="mt-1.5">
              {resumo.emCobranca === 0
                ? "nenhuma apólice em atraso"
                : `${m(resumo.emCobranca)} ${resumo.emCobranca === 1 ? "apólice" : "apólices"} · ${m(formatCurrency(resumo.premioEmRisco, true))}/mês`}
            </Sub>
          </div>
        </div>
        <Sub className="mt-2.5">
          <span className="font-semibold text-primary">Abrir cobrança →</span>
        </Sub>
      </button>
    </CardBento>
  );
};

const CardPropostas = ({ resumo, navigate }: PropsCard) => {
  const m = useMascara();
  return (
    <CardBento>
      <button type="button" onClick={() => navigate("/seguros/propostas")} className="block w-full cursor-pointer text-left">
        <Rotulo>Propostas em andamento</Rotulo>
        <ValorGrande>{m(resumo.propostasAbertas)}</ValorGrande>
        <Sub>{m(formatCurrency(resumo.pipelineMensal, true))}/mês em prêmio</Sub>
        {resumo.aguardandoPagamento > 0 && (
          <Sub tom="ruim" className="!mt-1">
            {m(resumo.aguardandoPagamento)} aguardando pagamento
          </Sub>
        )}
      </button>
    </CardBento>
  );
};

const CardComissao = ({ resumo, navigate }: PropsCard) => {
  const m = useMascara();
  const nomeMes = new Date().toLocaleDateString("pt-BR", { month: "long" });
  return (
    <CardBento>
      <button type="button" onClick={() => navigate("/seguros/take-rates")} className="block w-full cursor-pointer text-left">
        <Rotulo>Comissão de {nomeMes}</Rotulo>
        <ValorGrande>{m(formatCurrency(resumo.comissaoMes, true))}</ValorGrande>
        <Sub>mês anterior: {m(formatCurrency(resumo.comissaoMesAnterior, true))}</Sub>
      </button>
    </CardBento>
  );
};

const CardCorretores = ({ resumo }: { resumo: ResumoSeguros }) => {
  const m = useMascara();
  const maior = resumo.ranking[0]?.premio ?? 0;
  return (
    <CardBento col={2} className="!px-5 !py-4">
      <CabecalhoLista titulo="Produção por corretor no mês" extra={`${resumo.ranking.length} com produção`} />
      <div className="mt-3">
        {resumo.ranking.length === 0 ? (
          <Sub>Nenhuma apólice nova neste mês.</Sub>
        ) : (
          resumo.ranking.slice(0, TOP_CORRETORES).map((l, i) => (
            <div key={l.chave} className="flex items-center gap-3 border-b border-[rgba(53,70,96,0.07)] py-1.5 last:border-b-0">
              <span className="w-5 shrink-0 text-xs font-bold" style={{ color: i === 0 ? "#BEB692" : "#9aa2ad" }}>
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{l.nome}</span>
              <span className="hidden w-[72px] shrink-0 text-right text-xs tabular-nums text-muted-foreground sm:block">
                {m(l.apolices)} {l.apolices === 1 ? "apólice" : "apólices"}
              </span>
              <div className="h-1.5 w-[90px] shrink-0 overflow-hidden rounded-full bg-[rgba(53,70,96,0.1)]">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(4, Math.round((l.premio * 100) / (maior || 1)))}%` }}
                />
              </div>
              <span className="w-[88px] shrink-0 text-right text-[12.5px] font-semibold tabular-nums">
                {m(formatCurrency(l.premio, true))}
              </span>
            </div>
          ))
        )}
      </div>
    </CardBento>
  );
};

const CardCobrancaCritica = ({ resumo, todas, navigate }: PropsCard & { todas: boolean }) => {
  const m = useMascara();
  return (
    <CardBento col={2} className="!px-5 !py-4">
      <CabecalhoLista
        titulo="Cobrança crítica (30 dias ou mais)"
        extra={resumo.criticos.length > 0 ? `${resumo.criticos.length} apólices` : undefined}
      />
      <div className="mt-2">
        {resumo.criticos.length === 0 ? (
          <Sub>Nenhuma apólice com 30 dias ou mais de atraso.</Sub>
        ) : (
          resumo.criticos.slice(0, 6).map((a) => (
            <div
              key={`${a.seguradora}:${a.numero}`}
              className="flex items-center justify-between gap-3 border-b border-[rgba(53,70,96,0.07)] py-1.5 text-sm last:border-b-0"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate text-[13px] font-semibold">
                  {a.segurado_nome ?? "—"}
                  {todas && <BadgeSeguradora codigo={a.seguradora} />}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatarDocumento(a.segurado_cpf_digits)} · {a.corretor_nome ?? "—"}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="flex items-center justify-end gap-1 text-[12.5px] font-semibold tabular-nums text-erro">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {a.dias_atraso} dias
                </p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {m(formatCurrency(Number(a.premio_mensal || 0)))}/mês
                </p>
              </div>
            </div>
          ))
        )}
      </div>
      {resumo.criticos.length > 0 && <VerMais onClick={() => navigate("/seguros/cobranca")}>Ver toda a cobrança</VerMais>}
    </CardBento>
  );
};
